import { useState, useCallback } from 'react';
import { getApiUrl } from '../config';
import { Match, Player, PlayerStats } from '../types';

interface MatchActionsState {
  // Loading states
  finalizingMatch: boolean;
  finalizingReport: boolean;
  uploadingFinalScore: boolean;
  loadingReport: boolean;
  transferringAdmin: boolean;
  editingLocation: boolean;
  editingTeamNames: boolean;
  editingPlayerLoading: boolean;
  joiningMatch: boolean;
  isJoiningProcess: boolean;
  
  // Other states
  editingPlayer: Player | null;
  joinAsSubstitute: boolean;
  selectedTeamIndex: number | undefined;
  selectedPositionIndex: number | undefined;
  occupyingSpaces: { [key: string]: boolean };
}

interface MatchActionsActions {
  // Match management
  finalizeMatch: () => Promise<void>;
  finalizeReport: () => Promise<void>;
  uploadFinalScore: (team1Goals: number, team2Goals: number, scoreMode?: 'exact' | 'difference') => Promise<void>;
  recalculateMatchResult: () => Promise<void>;
  
  // Report management
  loadReport: (stats: PlayerStats[]) => Promise<void>;
  
  // Player management
  removePlayer: (playerId: string) => Promise<void>;
  selfRemove: (playerId: string) => Promise<void>;
  updatePlayer: (playerId: string, position: string, rating: number, name?: string, updateProfile?: boolean) => Promise<void>;
  handleJoinSubmit: (formData: { name: string; position: string; rating: number; isSubstitute: boolean }) => Promise<void>;
  handleReplacePlayer: (playerId: string, formData: { name: string; position: string; rating: number; isSubstitute: boolean }) => Promise<void>;
  
  // Admin actions
  transferAdmin: (newOwnerId: string) => Promise<void>;
  updateLocation: (data: { location: string; mapsUrl: string; date: string; time: string; price: string }) => Promise<void>;
  updateTeamNames: (data: {
    team1Name: string;
    team2Name: string;
    team1Color: string;
    team2Color: string;
  }) => Promise<void>;
  
  // Player actions
  handleEditPlayer: (player: Player) => void;
  
  // Join actions
  openJoinModal: (asSubstitute?: boolean, teamIndex?: number, positionIndex?: number) => void;
  closeJoinModal: () => void;
  
  // Space management
  getSpaceKey: (teamIndex: number, positionIndex: number) => string;
  
  // Setters
  setEditingPlayer: (player: Player | null) => void;
  setJoinAsSubstitute: (value: boolean) => void;
  setSelectedTeamIndex: (index: number | undefined) => void;
  setSelectedPositionIndex: (index: number | undefined) => void;
  setOccupyingSpaces: (spaces: { [key: string]: boolean }) => void;
}

interface UseMatchActionsProps {
  matchId: string | undefined;
  userIsCreator: boolean;
  user: any;
  onShowNotification: (message: string, type: 'success' | 'error') => void;
  onFetchMatch: () => Promise<void>;
  onFetchMatchReport: () => Promise<void>;
  onSetMatch: (match: Match) => void;
  onSetUserIsCreator: (value: boolean) => void;
  onSetMatchScore: (score: any) => void;
  onSetReportCompleted: (completed: boolean) => void;
  onSetMatchReport: (report: any) => void;
  onSetShowJoinModal: (show: boolean) => void;
  onCloseJoinModal: () => void;
  onOccupySpace: (matchId: string, spaceKey: string) => void;
  onReleaseSpace: (matchId: string, spaceKey: string) => void;
  onSetShowEditPlayerModal: (show: boolean) => void;
}

export const useMatchActions = ({
  matchId,
  userIsCreator,
  user,
  onShowNotification,
  onFetchMatch,
  onFetchMatchReport,
  onSetMatch,
  onSetUserIsCreator,
  onSetMatchScore,
  onSetReportCompleted,
  onSetMatchReport,
  onSetShowJoinModal,
  onCloseJoinModal,
  onOccupySpace,
  onReleaseSpace,
  onSetShowEditPlayerModal
}: UseMatchActionsProps): MatchActionsState & MatchActionsActions => {
  // Loading states
  const [finalizingMatch, setFinalizingMatch] = useState(false);
  const [finalizingReport, setFinalizingReport] = useState(false);
  const [uploadingFinalScore, setUploadingFinalScore] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [transferringAdmin, setTransferringAdmin] = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  const [editingTeamNames, setEditingTeamNames] = useState(false);
  const [editingPlayerLoading, setEditingPlayerLoading] = useState(false);
  const [joiningMatch, setJoiningMatch] = useState(false);
  const [isJoiningProcess, setIsJoiningProcess] = useState(false);
  
  // Other states
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [joinAsSubstitute, setJoinAsSubstitute] = useState(false);
  const [selectedTeamIndex, setSelectedTeamIndex] = useState<number | undefined>(undefined);
  const [selectedPositionIndex, setSelectedPositionIndex] = useState<number | undefined>(undefined);
  const [occupyingSpaces, setOccupyingSpaces] = useState<{[key: string]: boolean}>({});

  // API call with authentication
  const apiCallWithAuth = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    };

    return fetch(url, { 
      ...options, 
      headers
    });
  }, []);

  // Finalizar partido
  const finalizeMatch = useCallback(async () => {
    if (!userIsCreator) {
      onShowNotification('Solo el organizador puede finalizar el partido', 'error');
      return;
    }

    setFinalizingMatch(true);
    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/finish`), {
        method: 'POST'
      });

      if (response.ok) {
        onShowNotification('Partido finalizado exitosamente', 'success');
        await onFetchMatch();
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error finalizando partido', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error finalizing match:', error);
      onShowNotification('Error finalizando partido', 'error');
    } finally {
      setFinalizingMatch(false);
    }
  }, [userIsCreator, matchId, apiCallWithAuth, onShowNotification, onFetchMatch]);

  // Finalizar reporte
  const finalizeReport = useCallback(async () => {
    if (!matchId || !userIsCreator) return;
    
    setFinalizingReport(true);
    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/finalize-report`), {
        method: 'POST'
      });

      if (response.ok) {
        onShowNotification('Reporte finalizado exitosamente', 'success');
        onSetReportCompleted(true);
        await onFetchMatchReport();
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error finalizando reporte', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error finalizing report:', error);
      onShowNotification('Error finalizando reporte', 'error');
    } finally {
      setFinalizingReport(false);
    }
  }, [matchId, userIsCreator, apiCallWithAuth, onShowNotification, onSetReportCompleted, onFetchMatchReport]);

  // Subir resultado final del partido
  // scoreMode viaja al server para que el resultado "por diferencia" se muestre como
  // "Ganó X por N goles" y no como un marcador inventado (0-2).
  const uploadFinalScore = useCallback(async (
    team1Goals: number,
    team2Goals: number,
    scoreMode: 'exact' | 'difference' = 'exact'
  ) => {
    if (!matchId || !userIsCreator) return;

    setUploadingFinalScore(true);
    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/final-score`), {
        method: 'POST',
        body: JSON.stringify({ team1Goals, team2Goals, scoreMode })
      });

      if (response.ok) {
        const result = await response.json();
        onShowNotification('Resultado final guardado exitosamente', 'success');
        
        if (result.matchScore) {
          onSetMatchScore(result.matchScore);
        }
        
        await onFetchMatch();
        await onFetchMatchReport();
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error guardando resultado final', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error uploading final score:', error);
      onShowNotification('Error guardando resultado final', 'error');
    } finally {
      setUploadingFinalScore(false);
    }
  }, [matchId, userIsCreator, apiCallWithAuth, onShowNotification, onSetMatchScore, onFetchMatch, onFetchMatchReport]);

  // Recalcular resultado del partido
  const recalculateMatchResult = useCallback(async () => {
    if (!matchId || !userIsCreator) return;
    
    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/recalculate-match-result`), {
        method: 'POST'
      });

      if (response.ok) {
        onShowNotification('Resultado del partido recalculado exitosamente', 'success');
        await onFetchMatch();
        await onFetchMatchReport();
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error recalculando resultado del partido', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error recalculating match result:', error);
      onShowNotification('Error recalculando resultado del partido', 'error');
    }
  }, [matchId, userIsCreator, apiCallWithAuth, onShowNotification, onFetchMatch, onFetchMatchReport]);

  // Cargar reporte post-partido
  const loadReport = useCallback(async (stats: PlayerStats[]) => {
    if (!matchId || !userIsCreator) return;
    
    setLoadingReport(true);
    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/report`), {
        method: 'POST',
        body: JSON.stringify({ stats })
      });

      if (response.ok) {
        const result = await response.json();
        onShowNotification('Reporte guardado exitosamente', 'success');
        
        if (result.matchScore) {
          onSetMatchScore(result.matchScore);
        }
        
        await onFetchMatchReport();
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error cargando reporte', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading report:', error);
      onShowNotification('Error cargando reporte', 'error');
    } finally {
      setLoadingReport(false);
    }
  }, [matchId, userIsCreator, apiCallWithAuth, onShowNotification, onSetMatchScore, onFetchMatchReport]);

  // Remove player (solo para admin)
  const removePlayer = useCallback(async (playerId: string) => {
    if (!userIsCreator) {
      onShowNotification('Solo el organizador puede eliminar jugadores', 'error');
      return;
    }

    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/remove-player`), {
        method: 'POST',
        body: JSON.stringify({ playerId })
      });

      if (response.ok) {
        onShowNotification('Jugador eliminado exitosamente', 'success');
        await onFetchMatch();
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error removing player', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error removing player:', error);
      onShowNotification('Error removing player', 'error');
    }
  }, [userIsCreator, matchId, apiCallWithAuth, onShowNotification, onFetchMatch]);

  // Self remove (para que el jugador se elimine a sí mismo)
  const selfRemove = useCallback(async (playerId: string) => {
    if (!user) {
      onShowNotification('Debes estar logueado para salir del partido', 'error');
      return;
    }

    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/self-remove`), {
        method: 'POST',
        body: JSON.stringify({ playerId })
      });

      if (response.ok) {
        onShowNotification('Te has salido del partido exitosamente', 'success');
        setTimeout(() => {
          window.location.href = '/home';
        }, 1000);
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error al salir del partido', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error self removing:', error);
      onShowNotification('Error al salir del partido', 'error');
    }
  }, [user, matchId, apiCallWithAuth, onShowNotification]);

  // Actualizar posición y valoración de un jugador
  const updatePlayer = useCallback(async (playerId: string, position: string, rating: number, name?: string, updateProfile?: boolean) => {
    if (!user) {
      onShowNotification('Debes estar logueado para editar jugadores', 'error');
      return;
    }

    setEditingPlayerLoading(true);

    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/update-player`), {
        method: 'PUT',
        body: JSON.stringify({ playerId, position, rating, name, updateProfile })
      });

      if (response.ok) {
        const message = updateProfile 
          ? 'Jugador y perfil actualizados exitosamente' 
          : 'Jugador actualizado exitosamente';
        onShowNotification(message, 'success');
        setEditingPlayer(null);
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error al actualizar el jugador', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating player:', error);
      onShowNotification('Error al actualizar el jugador', 'error');
    } finally {
      setEditingPlayerLoading(false);
    }
  }, [user, matchId, apiCallWithAuth, onShowNotification]);

  const handleEditPlayer = useCallback((player: Player) => {
    setEditingPlayer(player);
    // Abrir el modal de edición
    onSetShowEditPlayerModal(true);
  }, [onSetShowEditPlayerModal]);

  // Transferir administración
  const transferAdmin = useCallback(async (newOwnerId: string) => {
    if (!user) {
      onShowNotification('Debes estar logueado para transferir la administración', 'error');
      return;
    }

    setTransferringAdmin(true);

    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/users/matches/${matchId}/transfer-ownership`), {
        method: 'POST',
        body: JSON.stringify({ newOwnerId })
      });

      if (response.ok) {
        const result = await response.json();
        onShowNotification(`Administración transferida exitosamente a ${result.newOwnerName}`, 'success');
        onSetUserIsCreator(false);
        await onFetchMatch();
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error al transferir la administración', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error transferring admin:', error);
      onShowNotification('Error al transferir la administración', 'error');
    } finally {
      setTransferringAdmin(false);
    }
  }, [user, matchId, apiCallWithAuth, onShowNotification, onSetUserIsCreator, onFetchMatch]);

  // Actualizar datos del partido
  const updateLocation = useCallback(async (data: { location: string; mapsUrl: string; date: string; time: string; price: string }) => {
    if (!user) {
      onShowNotification('Debes estar logueado para editar los datos del partido', 'error');
      return;
    }

    setEditingLocation(true);

    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/update`), {
        method: 'PUT',
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        onShowNotification('Datos del partido actualizados exitosamente', 'success');
        onSetMatch(result.match);
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error al actualizar los datos del partido', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating match data:', error);
      onShowNotification('Error al actualizar los datos del partido', 'error');
    } finally {
      setEditingLocation(false);
    }
  }, [user, matchId, apiCallWithAuth, onShowNotification, onSetMatch]);

  // Actualizar nombres de los equipos
  const updateTeamNames = useCallback(
    async (data: { team1Name: string; team2Name: string; team1Color: string; team2Color: string }) => {
      if (!user) {
        onShowNotification('Debes estar logueado para editar los equipos', 'error');
        return;
      }

      setEditingTeamNames(true);

      try {
        const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/team-colors`), {
          method: 'PATCH',
          body: JSON.stringify(data),
        });

        if (response.ok) {
          const result = await response.json();
          onShowNotification('Equipos actualizados', 'success');
          onSetMatch(result.match);
        } else {
          const error = await response.json();
          onShowNotification(error.error || 'Error al actualizar los equipos', 'error');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating team names/colors:', error);
        onShowNotification('Error al actualizar los equipos', 'error');
      } finally {
        setEditingTeamNames(false);
      }
    },
    [user, matchId, apiCallWithAuth, onShowNotification, onSetMatch]
  );

  // Join modal handlers
  const openJoinModal = useCallback((asSubstitute: boolean = false, teamIndex?: number, positionIndex?: number) => {
    if (!asSubstitute && !userIsCreator) {
      onShowNotification('Solo el organizador puede agregar jugadores', 'error');
      return;
    }
    
    setJoinAsSubstitute(asSubstitute);
    setSelectedTeamIndex(teamIndex);
    setSelectedPositionIndex(positionIndex);
    onSetShowJoinModal(true); // ✅ ABRIR EL MODAL
    
    if (!asSubstitute && teamIndex !== undefined && positionIndex !== undefined && matchId) {
      const spaceKey = `team${teamIndex + 1}_pos${positionIndex}`;
      onOccupySpace(matchId, spaceKey);
    }
  }, [userIsCreator, matchId, onShowNotification, onOccupySpace, onSetShowJoinModal]);

  const closeJoinModal = useCallback(() => {
    if (matchId) {
      Object.keys(occupyingSpaces).forEach(spaceKey => {
        onReleaseSpace(matchId, spaceKey);
      });
    }
    onCloseJoinModal();
    setOccupyingSpaces({});
    setSelectedTeamIndex(undefined);
    setSelectedPositionIndex(undefined);
  }, [matchId, occupyingSpaces, onCloseJoinModal, onReleaseSpace]);

  const handleJoinSubmit = useCallback(async (formData: { name: string; position: string; rating: number; isSubstitute: boolean }) => {
    setJoiningMatch(true);
    setIsJoiningProcess(true);

    try {
      const joinData = {
        ...formData,
        ...(formData.isSubstitute === false && selectedTeamIndex !== undefined && selectedPositionIndex !== undefined && {
          teamIndex: selectedTeamIndex,
          positionIndex: selectedPositionIndex
        })
      };

      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/join`), {
        method: 'POST',
        body: JSON.stringify(joinData)
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.leaveLink && result.player) {
          localStorage.setItem(`player_${result.player.id}_leaveLink`, result.leaveLink);
          localStorage.setItem(`player_${result.player.id}_name`, result.player.name);
          localStorage.setItem(`player_${result.player.id}_matchId`, matchId || '');
        }
        
        if (!user && matchId) {
          localStorage.setItem(`guest_joined_${matchId}`, 'true');
        }
        
        closeJoinModal();
        
        onShowNotification(
          joinAsSubstitute 
            ? 'Te has unido como suplente exitosamente!' 
            : 'Te has unido al partido exitosamente!', 
          'success'
        );
        
        await onFetchMatch();
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error al unirse al partido', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error joining match:', error);
      onShowNotification('Error al unirse al partido', 'error');
    } finally {
      setJoiningMatch(false);
      setTimeout(() => setIsJoiningProcess(false), 1000);
    }
  }, [matchId, selectedTeamIndex, selectedPositionIndex, user, joinAsSubstitute, apiCallWithAuth, closeJoinModal, onShowNotification, onFetchMatch]);

  const handleReplacePlayer = useCallback(async (playerId: string, formData: { name: string; position: string; rating: number; isSubstitute: boolean }) => {
    setJoiningMatch(true);
    setIsJoiningProcess(true);

    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/replace-player`), {
        method: 'POST',
        body: JSON.stringify({
          playerId,
          ...formData
        })
      });

      if (response.ok) {
        closeJoinModal();
        onShowNotification('¡Has reemplazado al jugador invitado exitosamente!', 'success');
        await onFetchMatch();
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error al reemplazar jugador', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error replacing player:', error);
      onShowNotification('Error al reemplazar jugador', 'error');
    } finally {
      setJoiningMatch(false);
      setTimeout(() => setIsJoiningProcess(false), 1000);
    }
  }, [matchId, apiCallWithAuth, closeJoinModal, onShowNotification, onFetchMatch]);

  // Space management
  const getSpaceKey = useCallback((teamIndex: number, positionIndex: number) => {
    return `team${teamIndex + 1}_pos${positionIndex}`;
  }, []);

  return {
    // State
    finalizingMatch,
    finalizingReport,
    uploadingFinalScore,
    loadingReport,
    transferringAdmin,
    editingLocation,
    editingTeamNames,
    editingPlayerLoading,
    joiningMatch,
    isJoiningProcess,
    editingPlayer,
    joinAsSubstitute,
    selectedTeamIndex,
    selectedPositionIndex,
    occupyingSpaces,
    
    // Actions
    finalizeMatch,
    finalizeReport,
    uploadFinalScore,
    recalculateMatchResult,
    loadReport,
    removePlayer,
    selfRemove,
    updatePlayer,
    handleEditPlayer,
    transferAdmin,
    updateLocation,
    updateTeamNames,
    openJoinModal,
    closeJoinModal,
    handleJoinSubmit,
    handleReplacePlayer,
    getSpaceKey,
    setEditingPlayer,
    setJoinAsSubstitute,
    setSelectedTeamIndex,
    setSelectedPositionIndex,
    setOccupyingSpaces
  };
};
